/**
 * Tiny Markdown renderer for chat bubbles (Story 303 + 304).
 *
 * Goals:
 * 1. Stream-friendly: never renders a half-open code fence as an open <pre>;
 *    when an unterminated ``` is detected the renderer treats the trailing
 *    section as plain text until the closer arrives.
 * 2. XSS-safe: input is HTML-escaped first; only a hand-picked subset of
 *    Markdown is then translated to the corresponding tags. We do not pull
 *    in a full Markdown library because (a) bundle size and (b) the
 *    challenger called out injection risk.
 *
 * Supported syntax:
 *   ```code fences``` (single language tag, optional)
 *   `inline code`
 *   **bold**, *italic*
 *   newline-separated paragraphs
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function inlineFormat(text: string): string {
  // bold first to avoid being eaten by italic
  return text
    .replace(/`([^`\n]+)`/g, (_, code) => `<code>${escapeHtml(code)}</code>`)
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^\\*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
}

interface ParsedSegment {
  kind: 'text' | 'code';
  body: string;
  open?: boolean;
}

/**
 * Splits a streaming buffer into alternating prose / code-fence segments.
 * Code fences without a closing ``` are returned with `open: true` so
 * `renderMarkdownSafely` can keep them as plain text rather than render an
 * unbalanced <pre>.
 */
export function parseSegments(input: string): ParsedSegment[] {
  const segments: ParsedSegment[] = [];
  let i = 0;
  while (i < input.length) {
    const fenceStart = input.indexOf('```', i);
    if (fenceStart === -1) {
      segments.push({ kind: 'text', body: input.slice(i) });
      break;
    }
    if (fenceStart > i) {
      segments.push({ kind: 'text', body: input.slice(i, fenceStart) });
    }
    const fenceEnd = input.indexOf('```', fenceStart + 3);
    if (fenceEnd === -1) {
      segments.push({ kind: 'code', body: input.slice(fenceStart + 3), open: true });
      break;
    }
    segments.push({ kind: 'code', body: input.slice(fenceStart + 3, fenceEnd) });
    i = fenceEnd + 3;
  }
  return segments;
}

export function renderMarkdownSafely(input: string): string {
  const segments = parseSegments(input);
  const parts: string[] = [];
  for (const seg of segments) {
    if (seg.kind === 'code' && !seg.open) {
      // Strip optional language hint.
      let body = seg.body;
      const newline = body.indexOf('\n');
      if (newline > 0 && /^[a-zA-Z0-9_-]+$/.test(body.slice(0, newline).trim())) {
        body = body.slice(newline + 1);
      }
      parts.push(`<pre><code>${escapeHtml(body.replace(/^\n+|\n+$/g, ''))}</code></pre>`);
      continue;
    }
    // text or open code: render as paragraphs of escaped + inline-formatted text.
    const escaped = escapeHtml(seg.body);
    const formatted = inlineFormat(escaped);
    const paragraphs = formatted
      .split(/\n{2,}/)
      .map((p) => p.replace(/\n/g, '<br />'))
      .filter((p) => p.trim().length > 0)
      .map((p) => `<p>${p}</p>`);
    parts.push(paragraphs.join(''));
  }
  return parts.join('');
}
